import React, { useState } from 'react';
import { Box, Divider, Paper, Typography } from '@mui/material';

import { getArchiveReasonLabel } from '@uplift/api';
import { useL10n } from '@uplift/hooks';
import { DataGrid } from '@mui/x-data-grid';
import { CalendarDateService } from '@uplift/services';
import { makeStyles } from 'tss-react/mui';
import { MoreOptionsDropdown, MoreOptionsDropdownItem, Tooltip } from '../../..';
import { ArchivedInsuranceDetailsModal } from './archived-insurance-details-modal';
import { InsuranceDataType } from '../Settings/types';


const getArchivedAtDisplay = (archivedAt: string) => {
  const date = CalendarDateService.date(archivedAt).tz(CalendarDateService.timezones.ET);

  if (date.isBefore(CalendarDateService.now(CalendarDateService.timezones.ET).subtract(1, 'week'))) {
    return date.format('M/D/YYYY, h:mm A');
  }

  return date.fromNow();
};

export type ArchivedNonPartnerInsurancesProps = {
  archivedInsurances: InsuranceDataType[];
};

export const ArchivedNonPartnerInsurances: React.VFC<ArchivedNonPartnerInsurancesProps> = ({ archivedInsurances }) => {
  const { classes } = useStyles();
  const l10n = useL10n();

  const restoreDisabledReason = 'Can’t restore non-partner insurance';

  return (
    <Box mt={4}>
      <Typography variant="h4" pb={2}>{l10n.accountSettings.insurance.archivedInsurance}</Typography>
      <Paper elevation={4}>
        <DataGrid
          rows={archivedInsurances ?? []}
          columns={[
            {
              field: 'companyName',
              headerName: 'Insurance Provider',
              width: 347,
              sortable: false
            },
            {
              field: 'subscriberId',
              headerName: 'Member ID',
              width: 157,
              sortable: false
            },
            {
              field: 'archivedBy',
              headerName: 'Archived',
              width: 336,
              sortable: false,
              renderCell: ({ row }) => (
                <Box height="100%" display="flex" flexDirection="column" justifyContent="center">
                  <Typography variant="body2" color="textPrimary">{getArchivedAtDisplay(row.archivedAt!)} by {row.archivedByFullName}</Typography>
                  <Typography variant="body2">{row.archiveReason && getArchiveReasonLabel(row.archiveReason)}</Typography>
                </Box>
              ),
            },
            {
              field: 'id',
              headerName: '',
              width: 80,
              align: 'center',
              sortable: false,
              renderCell: ({ row }) => (
                <ArchivedInsuranceOptions
                  insuranceData={row}
                  restoreDisabledReason={restoreDisabledReason}
                />
              )
            }
          ]}
          classes={classes}
          slots={{ footer: () => null }}
          disableColumnFilter
          disableColumnSorting
          disableColumnMenu
          disableRowSelectionOnClick
          disableColumnResize
        />
      </Paper>
    </Box>
  );
};

type ArchivedInsuranceOptionsProps = {
  insuranceData: InsuranceDataType;
  restoreDisabledReason: string | null;
};

const ArchivedInsuranceOptions: React.FC<ArchivedInsuranceOptionsProps> = (props) => {
  const [ detailsOpen, setIsDetailsOpen ] = useState(false);

  return (
    <div>
      <ArchivedInsuranceDetailsModal
        open={detailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        insuranceDetails={props.insuranceData}
        onOpenRestoreModal={() => {
          return;
        }}
        restoreDisabledReason={props.restoreDisabledReason}
      />
      <MoreOptionsDropdown>
        <MoreOptionsDropdownItem onClick={() => setIsDetailsOpen(true)}>
          View Details
        </MoreOptionsDropdownItem>
        <Divider />

        <Tooltip arrow={false} placement="bottom" title={props.restoreDisabledReason ?? null}>
          <div>
            <MoreOptionsDropdownItem
              disabled={!!props.restoreDisabledReason}
            >
              Restore Insurance
            </MoreOptionsDropdownItem>
          </div>
        </Tooltip>
      </MoreOptionsDropdown>
    </div>
  );
};


const useStyles = makeStyles()((theme) => ({
  root: {
    boxShadow: 'none',
    border: 'none',
    '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
      outline: 'none !important',
    },
    '& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus':
    {
      outline: 'none !important',
    },
  },
  columnHeaderTitle: {
    fontWeight: 450,
    color: theme.palette.text.primary
  },
  columnSeparator: {
    display: 'none !important',
  }
}));
